/* eslint-disable no-restricted-imports */
import { Modal } from 'antd';
import { useFormik } from 'formik';
import { forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { object } from 'yup';

import {
  COUNTRY_POSTAL_CODE_REGEX,
  COUNTRY_REGIONS,
} from '@frontend/addresses';
import { Address, useValidateAddress } from '@frontend/api';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  VisuallyHidden,
  VStack,
  PhoneInput,
  PhoneInputCountrySelect,
  PhoneInputField,
} from '@frontend/components';

import {
  getAddressValidationRules,
  transformShippingInfoToValues,
  transformValuesToShippingInfo,
} from 'components/Forms/AddressForm';

import {
  RegionSelect,
  CountrySelect,
} from 'modules/common/components/addressFormFields';

import { getDefaultFacilityCountry } from 'store/session/selectors';

import { FormikHooksReactSelectWrapper } from 'utils/FormikHooksReactSelectWrapper';

import { addressValidationModal } from '../../data/actions';
import { useConfirmAddress, useEditAddress } from '../../data/hooks';

type CustomerAddressFormProps = {
  initialAddress: any;
};

const CustomerAddressForm = forwardRef<HTMLElement, CustomerAddressFormProps>(
  ({ initialAddress }, ref) => {
    const dispatch = useDispatch();
    const defaultCountry = useSelector(getDefaultFacilityCountry);
    const initialValues = transformShippingInfoToValues(
      initialAddress,
      defaultCountry.abbr,
    );
    const confirmAddress = useConfirmAddress();
    const editAddress = useEditAddress();
    const { mutateAsync: validateAddress } = useValidateAddress();
    const formik = useFormik({
      initialValues,
      validationSchema: object().shape(getAddressValidationRules()),
      onSubmit: async (values) => {
        const address = transformValuesToShippingInfo(values) as Address;
        const response = await validateAddress(address);

        if (response.isValid) {
          if (response.candidates.length > 0) {
            dispatch(
              addressValidationModal.open({
                originalAddress: address,
                suggestedAddresses: response.candidates,
              }),
            );
          } else {
            confirmAddress(address);
          }
        } else {
          Modal.confirm({
            autoFocusButton: 'ok',
            title: "We couldn't validate your address",
            content: 'An invalid shipping address can cause delivery delays.',
            okText: 'Proceed anyway',
            cancelText: 'Back',
            onOk() {
              confirmAddress(address);
            },
            onCancel() {
              editAddress(address);
            },
          });
        }
      },
    });

    useImperativeHandle(ref, () => ({
      submit: () => formik.submitForm(),
    }));

    const { regions, postalCodeLabel, regionLabel } =
      COUNTRY_REGIONS[formik.values.country] ?? {};
    const postalCodeRegex = COUNTRY_POSTAL_CODE_REGEX[formik.values.country];

    return (
      <VStack gap={2} as="form" onSubmit={formik.handleSubmit}>
        <FormControl
          isRequired
          isInvalid={!!formik.errors.attention && formik.touched.attention}
        >
          <FormLabel>Attention</FormLabel>
          <Input
            name="attention"
            autoComplete="new-password"
            value={formik.values.attention}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <FormErrorMessage>{formik.errors.attention}</FormErrorMessage>
        </FormControl>
        <FormControl
          isRequired
          isInvalid={
            !!formik.errors.businessName && formik.touched.businessName
          }
        >
          <FormLabel>Business Name</FormLabel>
          <Input
            name="businessName"
            autoComplete="new-password"
            value={formik.values.businessName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <FormErrorMessage>{formik.errors.businessName}</FormErrorMessage>
        </FormControl>
        <HStack width="100%" gap={0.5} alignItems="flex-start">
          <FormControl
            isInvalid={!!formik.errors.phone && formik.touched.phone}
          >
            <FormLabel>Phone</FormLabel>
            <PhoneInput
              defaultCountry={defaultCountry.alpha2}
              value={formik.values.phone}
              onChange={(value) => formik.setFieldValue('phone', value)}
            >
              <PhoneInputCountrySelect />
              <PhoneInputField
                name="phone"
                onBlur={formik.handleBlur}
                autoComplete="new-password"
              />
            </PhoneInput>
            <FormErrorMessage>{formik.errors.phone}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!formik.errors.phoneExt && formik.touched.phoneExt}
          >
            <FormLabel>
              <VisuallyHidden>Extension</VisuallyHidden>
            </FormLabel>
            <InputGroup>
              <InputLeftAddon>ext.</InputLeftAddon>
              <Input
                name="phoneExt"
                autoComplete="new-password"
                value={formik.values.phoneExt}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </InputGroup>
            <FormErrorMessage>{formik.errors.phoneExt}</FormErrorMessage>
          </FormControl>
        </HStack>
        <FormControl
          isRequired
          isInvalid={!!formik.errors.address1 && formik.touched.address1}
        >
          <FormLabel>Street Address 1</FormLabel>
          <Input
            name="address1"
            autoComplete="new-password"
            value={formik.values.address1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <FormErrorMessage>{formik.errors.address1}</FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={!!formik.errors.address2 && formik.touched.address2}
        >
          <FormLabel>Street Address 2</FormLabel>
          <Input
            name="address2"
            autoComplete="new-password"
            value={formik.values.address2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <FormErrorMessage>{formik.errors.address2}</FormErrorMessage>
        </FormControl>
        <HStack gap={1.5} width="100%" alignItems="flex-start">
          <FormControl
            isRequired
            isInvalid={!!formik.errors.city && formik.touched.city}
          >
            <FormLabel>City</FormLabel>
            <Input
              name="city"
              autoComplete="new-password"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <FormErrorMessage>{formik.errors.city}</FormErrorMessage>
          </FormControl>
          <FormControl
            isRequired={!!regions?.length}
            isDisabled={!regions?.length}
            isInvalid={!!formik.errors.state && formik.touched.state}
          >
            <FormLabel>{regionLabel ?? 'State/Province'}</FormLabel>
            <FormikHooksReactSelectWrapper
              name="state"
              setFieldTouched={formik.setFieldTouched}
              setFieldValue={formik.setFieldValue}
              values={formik.values}
            >
              <RegionSelect
                autoComplete="new-password"
                country={formik.values.country}
              />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{formik.errors.state}</FormErrorMessage>
          </FormControl>
        </HStack>
        <HStack gap={1.5} width="100%" alignItems="flex-start">
          <FormControl
            isInvalid={!!formik.errors.postalCode && formik.touched.postalCode}
            isRequired={!!postalCodeRegex}
          >
            <FormLabel>{postalCodeLabel ?? 'Postal Code'}</FormLabel>
            <Input
              name="postalCode"
              autoComplete="new-password"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <FormErrorMessage>{formik.errors.postalCode}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!formik.errors.country && formik.touched.country}
          >
            <FormLabel>Country</FormLabel>
            <FormikHooksReactSelectWrapper
              name="country"
              setFieldTouched={formik.setFieldTouched}
              setFieldValue={formik.setFieldValue}
              values={formik.values}
            >
              <CountrySelect
                autoComplete="new-password"
                onChange={() => {
                  formik.setFieldValue('state', '');
                  formik.setFieldValue('postalCode', '');
                  formik.setFieldTouched('state', false);
                  formik.setFieldTouched('postalCode', false);
                }}
              />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{formik.errors.country}</FormErrorMessage>
          </FormControl>
        </HStack>
      </VStack>
    );
  },
);

CustomerAddressForm.displayName = 'CustomerAddressForm';

export default CustomerAddressForm;
